import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

// Generate unique session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('activity_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('activity_session_id', sessionId);
  }
  return sessionId;
};

// Parse user agent
const parseUserAgent = () => {
  const ua = navigator.userAgent;
  
  // Detect device type
  let deviceType = 'desktop';
  if (/Mobile|Android|iPhone|iPad/.test(ua)) {
    deviceType = /iPad|Tablet/.test(ua) ? 'tablet' : 'mobile';
  }
  
  // Detect browser
  let browser = 'unknown';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';
  else if (ua.includes('Opera')) browser = 'Opera';
  
  // Detect OS
  let os = 'unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  return { deviceType, browser, os, userAgent: ua };
};

export function useActivityTracker() {
  const { user } = useAuth();
  const location = useLocation();
  const sessionId = useRef(getSessionId());
  const lastPageRef = useRef<string | null>(null);
  const playerIdRef = useRef<string | null>(null);

  // Fetch player ID once
  useEffect(() => {
    const fetchPlayerId = async () => {
      if (!user) {
        playerIdRef.current = null;
        return;
      }
      
      const { data } = await supabase
        .from('players')
        .select('id')
        .eq('profile_id', user.id)
        .single();
      
      playerIdRef.current = data?.id || null;
    };
    
    fetchPlayerId();
  }, [user]);

  // Track page view
  const trackPageView = useCallback(async () => {
    if (!user) return;
    
    const pageUrl = location.pathname + location.search;
    
    // Skip if same page
    if (lastPageRef.current === pageUrl) return;
    lastPageRef.current = pageUrl;
    
    const { deviceType, browser, os, userAgent } = parseUserAgent();
    
    try {
      await supabase.from('player_activity_logs').insert([{
        player_id: playerIdRef.current,
        user_id: user.id,
        session_id: sessionId.current,
        activity_type: 'page_view',
        page_url: pageUrl,
        page_title: document.title,
        device_type: deviceType,
        browser,
        os,
        user_agent: userAgent,
      }]);

      // Update session
      await supabase
        .from('player_sessions')
        .upsert([{
          player_id: playerIdRef.current,
          user_id: user.id,
          session_id: sessionId.current,
          last_active_at: new Date().toISOString(),
          device_type: deviceType,
          browser,
          os,
          user_agent: userAgent,
          is_active: true,
        }], { onConflict: 'session_id' });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }, [user, location.pathname, location.search]);

  // Track action
  const trackAction = useCallback(async (actionType: string, details?: Record<string, unknown>) => {
    if (!user) return;
    
    const { deviceType, browser, os, userAgent } = parseUserAgent();
    
    try {
      await supabase.from('player_activity_logs').insert([{
        player_id: playerIdRef.current,
        user_id: user.id,
        session_id: sessionId.current,
        activity_type: actionType,
        page_url: location.pathname,
        page_title: document.title,
        details: details ? JSON.parse(JSON.stringify(details)) : {},
        device_type: deviceType,
        browser,
        os,
        user_agent: userAgent,
      }]);
    } catch (error) {
      console.error('Failed to track action:', error);
    }
  }, [user, location.pathname]);

  // Track page views on route change
  useEffect(() => {
    trackPageView();
  }, [trackPageView]);

  // Track session start/end
  useEffect(() => {
    if (!user) return;

    const { deviceType, browser, os, userAgent } = parseUserAgent();

    // Create/update session on mount
    const initSession = async () => {
      await supabase.from('player_sessions').upsert([{
        player_id: playerIdRef.current,
        user_id: user.id,
        session_id: sessionId.current,
        started_at: new Date().toISOString(),
        last_active_at: new Date().toISOString(),
        device_type: deviceType,
        browser,
        os,
        user_agent: userAgent,
        is_active: true,
      }], { onConflict: 'session_id' });
    };

    initSession();

    // End session on unmount
    const handleBeforeUnload = async () => {
      await supabase
        .from('player_sessions')
        .update({ 
          is_active: false, 
          ended_at: new Date().toISOString() 
        })
        .eq('session_id', sessionId.current);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user]);

  return { trackAction };
}
